import { ConnectionDisplayName } from '@app/modules/social-export/components/ConnectionDisplayName';
import { getPlatformExportDescriptor } from '@app/modules/social-export/domain/platform-export-descriptors';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import {
  useSocialExportGetConnectionById,
  useSocialExportGetConnectionSettings,
} from '@mi-front/api-v2';

import { QueryView } from '@ui/utils/async-view/QueryView';
import { useParams } from 'react-router-dom';
import { BasePlatformSettingsPage } from './BasePlatformSettingsPage';
import { ConnectionNameSettingsSection } from './ConnectionNameSettingsSection';
import { ConnectionStationsSettingsSection } from './ConnectionStationsSettingsSection';
import { ConnectionSuccessAlert } from './ConnectionSuccessAlert';
import { useConnectionSettingsDraft } from './useConnectionSettingsDraft';
import { useUpdateConnectionSettings } from './useUpdateConnectionSettings';

/**
 * Connection-level settings page: custom name, linked stations + defaults for uploads.
 */
export function PlatformSettingsPage() {
  const { platform: platformKey, connectionId } = useParams<{
    platform: string;
    connectionId: string;
  }>();
  const platform = socialPlatforms.get(platformKey)!;
  const { SettingsForm } = getPlatformExportDescriptor(platform);
  const connectionQuery = useSocialExportGetConnectionById(connectionId);
  const settingsQuery = useSocialExportGetConnectionSettings(connectionId);

  const { name, setName, stationIds, setStationIds } = useConnectionSettingsDraft(
    connectionQuery.data?.name ?? '',
    connectionQuery.data?.stationIds,
  );

  const { handleUpdate } = useUpdateConnectionSettings({
    connectionId,
    platform,
    name,
    stationIds,
  });

  const profileDisplayName = connectionQuery.data?.profile.displayName;

  return (
    <BasePlatformSettingsPage
      platform={platform}
      connectionName={
        connectionQuery.data ? (
          <ConnectionDisplayName connection={connectionQuery.data} component="span" />
        ) : undefined
      }
    >
      <ConnectionSuccessAlert platform={platform} />
      <ConnectionStationsSettingsSection stationIds={stationIds} onChange={setStationIds} />
      <ConnectionNameSettingsSection
        name={name}
        onChange={setName}
        placeholder={profileDisplayName}
      />
      <QueryView inline query={settingsQuery}>
        {(defaultSettings) => (
          <SettingsForm
            connectionId={connectionId}
            onUpdate={handleUpdate}
            defaultSettings={defaultSettings}
          />
        )}
      </QueryView>
    </BasePlatformSettingsPage>
  );
}
